import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Listing, Game } from '../types';
import { ListingCard } from '../components/listing/ListingCard';
import { Filter, Search, SlidersHorizontal, ChevronLeft, ChevronRight, X } from 'lucide-react';

export const Browse: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const gameParam = searchParams.get('game') || '';
  const categoryParam = searchParams.get('category') || '';
  const queryParam = searchParams.get('q') || '';
  const sortParam = searchParams.get('sort') || 'featured';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const [games, setGames] = useState<Game[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  // Local filter states
  const [searchInput, setSearchInput] = useState(queryParam);
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [wishlistedIds, setWishlistedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetch('/api/games')
      .then(res => res.json())
      .then(data => { if (data.success) setGames(data.games || []); });
  }, []);

  useEffect(() => {
    const fetchFilteredListings = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (gameParam) params.set('game', gameParam);
        if (categoryParam) params.set('category', categoryParam);
        if (queryParam) params.set('q', queryParam);
        if (sortParam) params.set('sort', sortParam);
        if (pageParam > 1) params.set('page', pageParam.toString());
        if (minPrice) params.set('minPrice', (parseInt(minPrice) * 100).toString());
        if (maxPrice) params.set('maxPrice', (parseInt(maxPrice) * 100).toString());

        const res = await fetch(`/api/listings?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setListings(data.listings || []);
            setPagination(data.pagination || { page: 1, total: 0, totalPages: 1 });
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredListings();
  }, [gameParam, categoryParam, queryParam, sortParam, pageParam, minPrice, maxPrice]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set('page', '1'); // reset page
    setSearchParams(next);
  };

  const handleWishlistToggle = async (id: string) => {
    try {
      const res = await fetch(`/api/listings/${id}/wishlist`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setWishlistedIds(prev => ({ ...prev, [id]: data.wishlisted }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const categories = [
    { slug: '', label: 'All Categories' },
    { slug: 'accounts', label: 'Game Accounts' },
    { slug: 'boosting', label: 'Boosting & Coaching' },
    { slug: 'items', label: 'In-Game Items' },
    { slug: 'currency', label: 'Gold & Currency' },
    { slug: 'keys', label: 'Activation Keys' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Search Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-brand-lightBorder dark:border-brand-darkBorder pb-6">
        <div>
          <h1 className="text-3xl font-black text-brand-lightText dark:text-brand-darkText">
            Marketplace Catalog
          </h1>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Browse verified listings with transparent seller stats and instant automated delivery
          </p>
        </div>

        {/* Global Search Bar */}
        <form
          onSubmit={(e) => { e.preventDefault(); updateParam('q', searchInput); }}
          className="relative max-w-md w-full"
        >
          <input
            type="text"
            placeholder="Search all listings..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 rounded-xl text-xs bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
          <Search className="w-4 h-4 text-brand-lightSecText absolute left-3.5 top-3" />
          {searchInput && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); updateParam('q', ''); }}
              className="absolute right-3 top-3 text-brand-lightSecText hover:text-brand-lightText"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        
        {/* Left Filter Sidebar */}
        <div className="space-y-6">
          <div className="p-5 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-brand-lightText dark:text-brand-darkText flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-brand-blue dark:text-brand-accent" />
                <span>Filters</span>
              </h3>
              {(gameParam || categoryParam || queryParam || minPrice || maxPrice) && (
                <button
                  onClick={() => setSearchParams({})}
                  className="text-[11px] text-rose-500 font-semibold hover:underline"
                >
                  Reset all
                </button>
              )}
            </div>

            {/* Game Selector */}
            <div>
              <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText mb-2">
                Game
              </label>
              <select
                value={gameParam}
                onChange={(e) => updateParam('game', e.target.value)}
                className="w-full px-3 py-2 rounded-xl text-xs bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
              >
                <option value="">All Games</option>
                {games.map((g) => (
                  <option key={g.slug} value={g.slug}>{g.name}</option>
                ))}
              </select>
            </div>

            {/* Category Selector */}
            <div>
              <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText mb-2">
                Category
              </label>
              <div className="space-y-1">
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => updateParam('category', c.slug)}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-colors ${
                      categoryParam === c.slug
                        ? 'bg-brand-blue/15 text-brand-blue dark:text-brand-accent font-bold'
                        : 'text-brand-lightSecText hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range Filter */}
            <div>
              <label className="block text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText mb-2">
                Price (USD)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min $"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText"
                />
                <span className="text-brand-lightSecText">-</span>
                <input
                  type="number"
                  placeholder="Max $"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Listings Results Area */}
        <div className="lg:col-span-3 space-y-6">
          
          {/* Top Sort & Count Bar */}
          <div className="flex items-center justify-between bg-white dark:bg-brand-darkCard p-3 rounded-2xl border border-brand-lightBorder dark:border-brand-darkBorder text-xs">
            <span className="text-brand-lightSecText dark:text-brand-darkSecText font-medium">
              Showing <strong>{listings.length}</strong> of {pagination.total} results
            </span>

            <div className="flex items-center gap-2">
              <span className="text-brand-lightSecText">Sort:</span>
              <select
                value={sortParam}
                onChange={(e) => updateParam('sort', e.target.value)}
                className="px-2.5 py-1 rounded-lg text-xs bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText font-semibold focus:outline-none"
              >
                <option value="featured">Featured Deals</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="newest">Newest Arrivals</option>
                <option value="rating">Top Seller Rating</option>
              </select>
            </div>
          </div>

          {/* Listings Grid */}
          {listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {listings.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  onWishlistToggle={handleWishlistToggle}
                  isWishlisted={wishlistedIds[l.id]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder">
              <p className="font-bold text-base text-brand-lightText dark:text-brand-darkText">No matching listings found</p>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">Try adjusting your filters or price range.</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-6">
              <button
                disabled={pageParam <= 1}
                onClick={() => updateParam('page', (pageParam - 1).toString())}
                className="p-2 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-bold px-3">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                disabled={pageParam >= pagination.totalPages}
                onClick={() => updateParam('page', (pageParam + 1).toString())}
                className="p-2 rounded-xl border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
